import { useState, useEffect } from 'react'
import { useUsersStore } from '../../store/usersStore'
import {
  Users, Plus, Settings, Shield, UserPlus, Crown, Activity, Package, Upload, BrainCircuit, Monitor, Mic, MessageSquare, FileText, Database, Lightbulb, MessageSquareCode, KeyRound,
} from 'lucide-react'
import styles from './SettingsTabs.module.css';
import SSOConfiguration from '../../components/ssoConfiguration/SSOConfiguration';
import UsersTab from '../../components/usersTab/UsersTab';
import { UserRole } from '../../enums/userRole';
import { hasMinRole } from '../../lib/permissions';
// import ApiKeysTab from './apiKeysTab/ApiKeysTab';
import { SETTINGS_TABS, SettingsTabId } from '../../types/types';
import { useNavigate, useSearchParams } from 'react-router-dom'
import LogsTab from './logsTab/LogsTab'
import { managementsTabsEnum } from '../../types/managmentsTabsEnum'
import LlmSettingsTab from './llmSettingsTab/LlmSettingsTab'
import ChatLlmSettingsTab from './chatLlmSettingsTab/ChatLlmSettingsTab'
import ChatVoiceSettingsTab from './chatVoiceSettingsTab/ChatVoiceSettingsTab'
import PackagesTab from './packagesTab/PackagesTab'
import SystemSettingsTab from './systemSettingsTab/SystemSettingsTab'
import PromptsTab from './promptsTab/PromptsTab'
import AiBuilderTab from './aiBuilderTab/AiBuilderTab'
import DataLakeSettingsTab from './dataLakeSettingsTab/DataLakeSettingsTab'
import { useAuthStore } from '../../store/authStore'
import { healthApi } from '../../lib/apiClient';



function isSettingsTabId(value: string | null): value is SettingsTabId {
  return value !== null && SETTINGS_TABS.includes(value as SettingsTabId);
}

export default function SettingsTabs() {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user)
  const { users } = useUsersStore()
  const currentUser = useAuthStore(state => state.user)
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = hasMinRole(user?.role?.id, UserRole.SuperAdmin);
  const isAdminOnly = !isSuperAdmin && hasMinRole(user?.role?.id, UserRole.Admin);
  const requestedTab = searchParams.get('tab');
  const activeTab: SettingsTabId | null = isSettingsTabId(requestedTab)
    ? requestedTab
    : (isAdminOnly ? managementsTabsEnum.Users : null);
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [showCreateApiKey, setShowCreateApiKey] = useState(false)
  const [showUploadPackage, setShowUploadPackage] = useState(false)
  const [apiVersion, setApiVersion] = useState<string | null>(null)

  useEffect(() => {
    if (!currentUser || !hasMinRole(currentUser?.role?.id, UserRole.Admin)) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (!isAdminOnly && !isSettingsTabId(requestedTab)) {
      navigate(`/settings?tab=${managementsTabsEnum.System}`, { replace: true });
    }
  }, [currentUser, isAdminOnly, requestedTab, navigate])

  useEffect(() => {
    healthApi.health()
      .then(data => {
        setApiVersion(data.serverVersion ?? null)
        // setServerBuild(data.serverBuild ?? null)
      })
      .catch(() => { })
  }, [])



  useEffect(() => {
    if (isAdminOnly && requestedTab !== managementsTabsEnum.Users) {
      setSearchParams({ tab: managementsTabsEnum.Users }, { replace: true });
    }
  }, [isAdminOnly, requestedTab, setSearchParams]);

  const getRoleBadge = (role: number) => {
    const roleConfig: Record<number, { label: string; color: string; icon: React.ComponentType<any> }> = {
      [UserRole.SuperAdmin]: { label: 'Super Admin', color: '#dc2626', icon: Crown },
      [UserRole.Admin]: { label: 'Admin', color: '#2563eb', icon: Shield },
      [UserRole.WorkzoneManager]: { label: 'Workzone Manager', color: '#7c3aed', icon: MessageSquareCode },
      [UserRole.ToolManager]: { label: 'Tool Manager', color: '#059669', icon: Settings },
      [UserRole.PromptEditor]: { label: 'Prompt Editor', color: '#b45309', icon: KeyRound },
      [UserRole.Viewer]: { label: 'Viewer', color: '#6b7280', icon: Users }
    }

    const config = roleConfig[role as number] || roleConfig[UserRole.Viewer]
    const Icon = config.icon

    return (
      <div
        className={styles.roleBadge}
        style={{ backgroundColor: `${config.color}20`, color: config.color }}
      >
        <Icon size={16} />
        {'  '}{config.label}
      </div>
    )
  }

  const managementsTabs: { id: SettingsTabId; label: string; icon: React.ComponentType<any>; count?: () => number }[] = [];
  if (hasMinRole(user?.role?.id, UserRole.SuperAdmin)) {
    managementsTabs.push(
      {
        id: managementsTabsEnum.System,
        label: 'System',
        icon: Monitor,
      },
      {
        id: managementsTabsEnum.Users,
        label: 'Users',
        icon: Users,
        count: () => users.length,
      },
      {
        id: managementsTabsEnum.LlmSettings,
        label: 'System LLM',
        icon: BrainCircuit,
      },
      {
        id: managementsTabsEnum.ChatLlm,
        label: 'Chat LLM',
        icon: MessageSquare,
      },
      {
        id: managementsTabsEnum.ChatVoice,
        label: 'Chat Voice',
        icon: Mic,
      },
      {
        id: managementsTabsEnum.Prompts,
        label: 'Prompts',
        icon: FileText,
      },
      {
        id: managementsTabsEnum.AiBuilder,
        label: 'AI Chat Tool Builder',
        icon: Lightbulb,
      },
      // {
      //   id: managementsTabsEnum.ApiKeys,
      //   label: 'API Keys',
      //   icon: Key,
      //   count: () => apiKeys.length,
      // },
      {
        id: managementsTabsEnum.Sso,
        label: 'SSO Configuration',
        icon: Shield,
      },
      {
        id: managementsTabsEnum.Packages,
        label: 'Tools Packages',
        icon: Package,
      },
      {
        id: managementsTabsEnum.DataLake,
        label: 'Data Lake',
        icon: Database,
      },
      {
        id: managementsTabsEnum.Logs,
        label: 'Logs',
        icon: Activity,
      }
    )
  }
  else if (hasMinRole(user?.role?.id, UserRole.Admin)) {
    managementsTabs.push(
      {
        id: managementsTabsEnum.Users,
        label: 'Users',
        icon: Users,
        count: () => users.length,
      },
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Settings</h1>
          <p>
            {apiVersion && <>  <strong>Server</strong> v{apiVersion}
              {" |"} <strong>UI</strong> v{__APP_VERSION__}
            </>}
          </p>
        </div>
        <div className={styles.headerActions}>
          {activeTab === 'users' && (
            <button
              onClick={() => setShowCreateUser(!showCreateUser)}
              className={styles.primaryBtn}
            >
              <UserPlus size={16} />
              Add User
            </button>
          )}
          {activeTab === 'api-keys' && (
            <button
              onClick={() => setShowCreateApiKey(!showCreateApiKey)}
              className={styles.primaryBtn}
            >
              <Plus size={16} />
              Create API Key
            </button>
          )}
          {activeTab === 'packages' && (
            <button
              onClick={() => setShowUploadPackage(true)}
              className={styles.primaryBtn}
            >
              <Upload size={16} />
              Upload Package
            </button>
          )}
        </div>
      </div>


      {activeTab !== null && (
        <div className={styles.content}>
          {/* {activeTab === 'general' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
          <GeneralSettingsTab />
        )} */}

          {activeTab === 'users' && (
            <UsersTab
              showCreateUser={showCreateUser}
              setShowCreateUser={setShowCreateUser}
              getRoleBadge={getRoleBadge}
            />
          )}
          {activeTab === 'llm-settings' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <LlmSettingsTab />
          )}

          {activeTab === 'chat-llm' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <ChatLlmSettingsTab />
          )}

          {activeTab === 'chat-voice' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <ChatVoiceSettingsTab />
          )}

          {activeTab === 'prompts' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <PromptsTab />
          )}

          {activeTab === 'ai-builder' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <AiBuilderTab />
          )}


          {/* {activeTab === 'api-keys' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
          <ApiKeysTab
            apiKeys={apiKeys}
            setApiKeys={setApiKeys}
            showCreateApiKey={showCreateApiKey}
            setShowCreateApiKey={setShowCreateApiKey}
          />
        )} */}

          {activeTab === 'logs' && (
            <LogsTab />
          )}

          {activeTab === 'sso' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <div className={styles.ssoTab}>
              <SSOConfiguration />
            </div>
          )}

          {activeTab === 'packages' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <PackagesTab
              showUpload={showUploadPackage}
              setShowUpload={setShowUploadPackage}
            />
          )}

          {activeTab === 'system' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <SystemSettingsTab />
          )}

          {activeTab === 'data-lake' && hasMinRole(user?.role?.id, UserRole.SuperAdmin) && (
            <DataLakeSettingsTab />
          )}


        </div>
      )}
    </div>
  )
}
